import { ResponsiveLine } from "@nivo/line";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Search } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DateRangeSelect,
  ReportFilterBar,
  ReportPageHeader,
  useReportFilters,
} from "./ReportChrome";
import { Input } from "@/components/ui/input";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Web & Ads - where the GA4 / Search Console / Google Ads / LinkedIn Ads
 * integrations surface their data: browsable, searchable, filterable.
 * Everything on this screen is SAMPLE data shaped like the real sync;
 * the nightly OAuth syncs (configured under Integrations) replace it at
 * deployment, and ad spend then also joins the Attribution report as
 * cost-per-opportunity.
 */

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

const SampleBadge = () => (
  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#d9b36b]/20 text-[#8a6d3b] dark:text-[#d9b36b] font-medium">
    Sample · syncs at deployment
  </span>
);

// --- deterministic sample data --------------------------------------------

const WEEKS = ["Jul 6", "Jul 13", "Jul 20", "Jul 27", "Aug 3", "Aug 10", "Aug 17"];
const SESSIONS = [1180, 1245, 1310, 1198, 1402, 1489, 1553];
const CONVERSIONS = [18, 21, 24, 19, 27, 31, 33];

const GSC_ROWS = [
  { query: "industrial automation partner", clicks: 142, impressions: 3120, position: 4.2 },
  { query: "cnc machining services ohio", clicks: 118, impressions: 2410, position: 3.1 },
  { query: "b2b manufacturing marketing", clicks: 96, impressions: 4180, position: 6.8 },
  { query: "contract manufacturing quote", clicks: 84, impressions: 1980, position: 5.4 },
  { query: "precision fabrication cincinnati", clicks: 71, impressions: 1210, position: 2.9 },
  { query: "oem supplier directory", clicks: 44, impressions: 3890, position: 11.6 },
  { query: "custom metal enclosures", clicks: 38, impressions: 2740, position: 9.3 },
  { query: "manufacturing capacity planning", clicks: 27, impressions: 2210, position: 14.8 },
  { query: "supplier quality audit checklist", clicks: 21, impressions: 1870, position: 12.4 },
  { query: "industrial equipment financing", clicks: 12, impressions: 1650, position: 18.2 },
];

const GADS_ROWS = [
  { campaign: "brand-search", spend: 1840, clicks: 612, conversions: 34 },
  { campaign: "competitor-conquest", spend: 2410, clicks: 388, conversions: 12 },
  { campaign: "services-exact", spend: 3120, clicks: 545, conversions: 26 },
  { campaign: "remarketing-display", spend: 960, clicks: 1240, conversions: 9 },
];

const LI_ROWS = [
  { campaign: "abm-q3-manufacturing", spend: 2875, clicks: 402, leads: 18 },
  { campaign: "case-study-retargeting", spend: 1130, clicks: 265, leads: 11 },
  { campaign: "webinar-promo", spend: 1560, clicks: 331, leads: 24 },
];

const FILTER_DEFAULTS = { range: "30d", source: "all" };

export const WebAdsTab = () => {
  const { currency } = useConfigurationContext();
  const filters = useReportFilters(FILTER_DEFAULTS);
  const source = filters.get("source");
  const showWeb = source === "all" || source === "web";
  const showSearch = source === "all" || source === "gsc";
  const showAds = source === "all" || source === "ads";
  const [gscQuery, setGscQuery] = useState("");
  const [striking, setStriking] = useState(false);
  const [adsQuery, setAdsQuery] = useState("");

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    });

  const gscFiltered = useMemo(
    () =>
      GSC_ROWS.filter(
        (r) =>
          r.query.toLowerCase().includes(gscQuery.toLowerCase()) &&
          (!striking || (r.position >= 8 && r.position <= 20)),
      ),
    [gscQuery, striking],
  );
  const gadsFiltered = GADS_ROWS.filter((r) =>
    r.campaign.toLowerCase().includes(adsQuery.toLowerCase()),
  );
  const liFiltered = LI_ROWS.filter((r) =>
    r.campaign.toLowerCase().includes(adsQuery.toLowerCase()),
  );

  const totalSessions = SESSIONS.reduce((a, b) => a + b, 0);
  const totalConv = CONVERSIONS.reduce((a, b) => a + b, 0);
  const adSpend =
    GADS_ROWS.reduce((s, r) => s + r.spend, 0) +
    LI_ROWS.reduce((s, r) => s + r.spend, 0);

  // Live deployments show a connect state, never fabricated analytics - the
  // sample tables exist so the demo can show the report's exact shape.
  if (!IS_DEMO) {
    return (
      <div className="flex flex-col gap-4">
        <ReportPageHeader
          slug="web"
          methodology={
            <p className="text-xs leading-5">
              Connect Google Analytics, Search Console, and your ad accounts
              under Integrations - nightly syncs fill every table here, and ad
              spend joins Attribution as cost-per-opportunity.
            </p>
          }
        />
        <div className="rounded-md border border-border p-8 text-center flex flex-col items-center gap-2">
          <p className="text-sm font-medium">No sources connected yet</p>
          <p className="text-sm text-muted-foreground max-w-md">
            Web, search, and ads reporting fills in from your connected
            accounts - nothing here is estimated or sampled. Connect Google
            Analytics, Search Console, Google Ads, or LinkedIn Ads to start
            the nightly sync.
          </p>
          <Link to="/settings/integrations" className="text-sm underline hover:no-underline">
            Open Integrations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ReportPageHeader
        slug="web"
        methodology={
          <p className="text-xs leading-5">
            Sample data shaped exactly like the live sync. Connect Google
            Analytics, Search Console, and your ad accounts under{" "}
            <Link to="/settings/integrations" className="underline">
              Integrations
            </Link>{" "}
            and nightly syncs replace every table at deployment - ad spend then
            also joins Attribution as cost-per-opportunity.
          </p>
        }
      >
        <ReportFilterBar onReset={filters.reset} showReset={!filters.isDefault}>
          <DateRangeSelect value={filters.get("range")} onChange={(v) => filters.set("range", v)} />
          <Select value={source} onValueChange={(v) => filters.set("source", v)}>
            <SelectTrigger className="h-8 w-48 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="web">Web analytics (GA4)</SelectItem>
              <SelectItem value="gsc">Search (Search Console)</SelectItem>
              <SelectItem value="ads">Ads (Google &amp; LinkedIn)</SelectItem>
            </SelectContent>
          </Select>
        </ReportFilterBar>
      </ReportPageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Sessions (7 wks)", value: totalSessions.toLocaleString() },
          { label: "Site conversions", value: String(totalConv) },
          { label: "Ad spend (mo)", value: fmt(adSpend) },
          { label: "Search clicks (mo)", value: String(GSC_ROWS.reduce((s, r) => s + r.clicks, 0)) },
        ].map((t) => (
          <Card key={t.label}>
            <CardContent className="px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</p>
              <p className="text-xl font-semibold tabular-nums">{t.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* GA4 */}
        {showWeb && (
        <Card>
          <CardHeader className="px-4 pb-0 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm">Traffic & conversions - GA4</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Is site traffic growing, and is it turning into conversions?
              </p>
            </div>
            <SampleBadge />
          </CardHeader>
          <CardContent className="px-4 pt-2">
            <div style={{ height: 220 }}>
              <ResponsiveLine
                data={[
                  { id: "Sessions", data: WEEKS.map((w, i) => ({ x: w, y: SESSIONS[i]! })) },
                  { id: "Conversions ×20", data: WEEKS.map((w, i) => ({ x: w, y: CONVERSIONS[i]! * 20 })) },
                ]}
                colors={["#2e6ba9", "#b07d3f"]}
                margin={{ top: 12, right: 16, bottom: 44, left: 48 }}
                curve="monotoneX"
                lineWidth={2}
                pointSize={5}
                enableGridX={false}
                useMesh
                theme={{
                  text: { fontSize: 10, fill: "var(--muted-foreground)" },
                  grid: { line: { stroke: "var(--border)", strokeWidth: 1 } },
                  tooltip: { container: { background: "transparent", boxShadow: "none", padding: 0 } },
                }}
                yScale={{ type: "linear", min: 0, max: "auto" }}
                axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4 }}
                axisBottom={{ tickSize: 0, tickPadding: 6 }}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 40,
                    itemWidth: 120,
                    itemHeight: 14,
                    symbolSize: 8,
                    symbolShape: "circle",
                  },
                ]}
                tooltip={({ point }: any) => (
                  <div className="p-2 bg-secondary rounded shadow text-secondary-foreground text-sm">
                    <strong>{String(point.serieId)}</strong> · {String(point.data.x)}: {String(point.data.y)}
                  </div>
                )}
              />
            </div>
          </CardContent>
        </Card>
        )}

        {/* GSC */}
        {showSearch && (
        <Card>
          <CardHeader className="px-4 pb-0 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm">Search queries - Search Console</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Which searches put you in front of buyers, and where you rank.
              </p>
            </div>
            <SampleBadge />
          </CardHeader>
          <CardContent className="px-4 pt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                <Input
                  className="h-8 pl-7 text-sm"
                  placeholder="Search queries…"
                  value={gscQuery}
                  onChange={(e) => setGscQuery(e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => setStriking((v) => !v)}
                title="Positions 8–20: page-two terms one push from page one"
                className={`rounded-md border px-2 py-1 text-xs whitespace-nowrap transition-colors ${
                  striking
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                Striking distance
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-2 font-medium">Query</th>
                    <th className="py-1 pr-2 font-medium text-right">Clicks</th>
                    <th className="py-1 pr-2 font-medium text-right">Impr.</th>
                    <th className="py-1 font-medium text-right">Pos.</th>
                  </tr>
                </thead>
                <tbody>
                  {gscFiltered.map((r) => (
                    <tr key={r.query} className="border-t border-border">
                      <td className="py-1 pr-2 truncate max-w-48">{r.query}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{r.clicks}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
                      <td className="py-1 text-right tabular-nums">{r.position.toFixed(1)}</td>
                    </tr>
                  ))}
                  {gscFiltered.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-2 text-muted-foreground text-sm">
                        No queries match.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Google Ads */}
        {showAds && (
        <Card>
          <CardHeader className="px-4 pb-0 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm">Campaigns - Google Ads</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                What each paid campaign costs per conversion.
              </p>
            </div>
            <SampleBadge />
          </CardHeader>
          <CardContent className="px-4 pt-2 flex flex-col gap-2">
            <Input
              className="h-8 text-sm"
              placeholder="Filter campaigns…"
              value={adsQuery}
              onChange={(e) => setAdsQuery(e.target.value)}
            />
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">Campaign</th>
                  <th className="py-1 pr-2 font-medium text-right">Spend</th>
                  <th className="py-1 pr-2 font-medium text-right">Clicks</th>
                  <th className="py-1 pr-2 font-medium text-right">Conv.</th>
                  <th className="py-1 font-medium text-right">CPA</th>
                </tr>
              </thead>
              <tbody>
                {gadsFiltered.map((r) => (
                  <tr key={r.campaign} className="border-t border-border">
                    <td className="py-1 pr-2">{r.campaign}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{fmt(r.spend)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.conversions}</td>
                    <td className="py-1 text-right tabular-nums">
                      {r.conversions ? fmt(r.spend / r.conversions) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        )}

        {/* LinkedIn Ads */}
        {showAds && (
        <Card>
          <CardHeader className="px-4 pb-0 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm">Campaigns - LinkedIn Ads</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                B2B awareness spend and what it returns.
              </p>
            </div>
            <SampleBadge />
          </CardHeader>
          <CardContent className="px-4 pt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">Campaign</th>
                  <th className="py-1 pr-2 font-medium text-right">Spend</th>
                  <th className="py-1 pr-2 font-medium text-right">Clicks</th>
                  <th className="py-1 pr-2 font-medium text-right">Leads</th>
                  <th className="py-1 font-medium text-right">Cost/lead</th>
                </tr>
              </thead>
              <tbody>
                {liFiltered.map((r) => (
                  <tr key={r.campaign} className="border-t border-border">
                    <td className="py-1 pr-2">{r.campaign}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{fmt(r.spend)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.clicks}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{r.leads}</td>
                    <td className="py-1 text-right tabular-nums">{fmt(r.spend / r.leads)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">
              Lead-gen forms sync into Contacts with attribution once the
              LinkedIn Marketing API connection is approved.
            </p>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
};
